import { useState, useEffect } from 'react';
import { clients, opportunities } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Target, Building, MoreVertical, Trash2, FileText, CalendarClock } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog';

const OPPORTUNITY_STATUS = {
  new: { label: 'Nuevo', color: 'bg-slate-500' },
  qualification: { label: 'Calificacion', color: 'bg-blue-500' },
  proposal: { label: 'Propuesta', color: 'bg-violet-500' },
  negotiation: { label: 'Negociacion', color: 'bg-amber-500' },
  won: { label: 'Ganada', color: 'bg-emerald-500' },
  lost: { label: 'Perdida', color: 'bg-rose-500' },
};

const VERTICALS = {
  logistics: { label: 'Servicios Logisticos', color: 'text-blue-600' },
  consulting: { label: 'Consultoria', color: 'text-violet-600' },
  technology: { label: 'Tecnologia', color: 'text-emerald-600' },
};

export default function Negocios() {
  const { user } = useAuth();
  const [clientsList, setClientsList] = useState([]);
  const [oppsList, setOppsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState({ type: null, id: null, name: null });
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');

  const [formData, setFormData] = useState({
    client_id: '', name: '', description: '', value: '', vertical: 'logistics',
    status: 'new', expected_close_date: '', contact_id: '',
  });

  const isAdmin = user?.role === 'admin';

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [cr, or] = await Promise.all([clients.getAll(), opportunities.getAll()]);
      setClientsList(cr.data);
      setOppsList(or.data);
    } catch (err) { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.client_id || !formData.name) { toast.error('Empresa y nombre son requeridos'); return; }
    try {
      await opportunities.create({ ...formData, value: parseFloat(formData.value) || 0, contact_id: formData.contact_id || undefined });
      toast.success('Negocio creado');
      setDialogOpen(false);
      setFormData({ client_id: '', name: '', description: '', value: '', vertical: 'logistics', status: 'new', expected_close_date: '', contact_id: '' });
      loadData();
    } catch (err) { toast.error('Error al crear negocio'); }
  };

  const handleUpdateStatus = async (oppId, newStatus) => {
    try {
      await opportunities.update(oppId, { status: newStatus });
      toast.success('Estado actualizado');
      loadData();
    } catch (err) { toast.error('Error al actualizar'); }
  };

  const handleDelete = async (oppId) => {
    try { await opportunities.delete(oppId); toast.success('Negocio eliminado'); loadData(); }
    catch (err) { toast.error('Error al eliminar'); }
  };

  const filtered = oppsList.filter(o => {
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    if (clientFilter !== 'all' && o.client_id !== clientFilter) return false;
    return true;
  });

  const totalPipeline = oppsList.filter(o => !['won','lost'].includes(o.status)).reduce((s,o) => s + (o.value||0), 0);
  const wonTotal = oppsList.filter(o => o.status === 'won').reduce((s,o) => s + (o.value||0), 0);
  const lostTotal = oppsList.filter(o => o.status === 'lost').reduce((s,o) => s + (o.value||0), 0);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="negocios-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight flex items-center gap-3">
            <Target className="h-8 w-8 text-primary" />
            Negocios
          </h1>
          <p className="text-muted-foreground">Gestion de oportunidades de negocio</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="add-negocio-btn">
          <Plus className="h-4 w-4 mr-2" />Agregar Negocio
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Total Negocios</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold">{oppsList.length}</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Pipeline Activo</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold text-blue-500">{formatCurrency(totalPipeline)}</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Ganados</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold text-emerald-500">{formatCurrency(wonTotal)}</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Perdidos</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold text-rose-500">{formatCurrency(lostTotal)}</p></CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex gap-2">
          {['all', ...Object.keys(OPPORTUNITY_STATUS)].map(s => (
            <Button key={s} variant={statusFilter === s ? 'default' : 'outline'} size="sm" onClick={() => setStatusFilter(s)}>
              {s === 'all' ? 'Todos' : OPPORTUNITY_STATUS[s]?.label}
            </Button>
          ))}
        </div>
        <div className="h-6 w-px bg-border" />
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="w-[180px] h-8"><SelectValue placeholder="Empresa" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las empresas</SelectItem>
            {clientsList.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.company || c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">No hay negocios registrados</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="uppercase text-xs">Numero</TableHead>
                  <TableHead className="uppercase text-xs">Negocio</TableHead>
                  <TableHead className="uppercase text-xs">Empresa</TableHead>
                  <TableHead className="uppercase text-xs">Vertical</TableHead>
                  <TableHead className="uppercase text-xs">Estado</TableHead>
                  <TableHead className="uppercase text-xs text-right">Valor</TableHead>
                  <TableHead className="uppercase text-xs">Cierre</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(opp => (
                  <TableRow key={opp.opportunity_id}>
                    <TableCell><Badge variant="outline" className="font-mono">{opp.opportunity_number}</Badge></TableCell>
                    <TableCell>
                      <div className="font-medium">{opp.name}</div>
                      {opp.contact_name && <div className="text-xs text-muted-foreground">Contacto: {opp.contact_name}</div>}
                    </TableCell>
                    <TableCell><div className="flex items-center gap-1"><Building className="h-3 w-3 text-muted-foreground" />{opp.client_name}</div></TableCell>
                    <TableCell><span className={`text-sm font-medium ${VERTICALS[opp.vertical]?.color || ''}`}>{VERTICALS[opp.vertical]?.label || opp.vertical}</span></TableCell>
                    <TableCell>
                      <Select value={opp.status} onValueChange={(v) => handleUpdateStatus(opp.opportunity_id, v)}>
                        <SelectTrigger className="w-[130px] h-8">
                          <div className="flex items-center gap-2"><div className={`w-2 h-2 rounded-full ${OPPORTUNITY_STATUS[opp.status]?.color}`} /><span className="text-xs">{OPPORTUNITY_STATUS[opp.status]?.label}</span></div>
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(OPPORTUNITY_STATUS).map(([k, { label: l, color: c }]) => (
                            <SelectItem key={k} value={k}><div className="flex items-center gap-2"><div className={`w-2 h-2 rounded-full ${c}`} />{l}</div></SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right font-mono font-medium">{formatCurrency(opp.value)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{opp.expected_close_date ? formatDate(opp.expected_close_date) : '-'}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem className="text-destructive" onClick={() => { setDeleteItem({ type: 'opportunity', id: opp.opportunity_id, name: opp.name }); setDeleteDialogOpen(true); }}>
                            <Trash2 className="h-4 w-4 mr-2" />Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-xl flex items-center gap-2"><Target className="h-5 w-5" />Nuevo Negocio</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="space-y-1.5">
              <Label>Empresa *</Label>
              <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v })}>
                <SelectTrigger><SelectValue placeholder="Seleccionar empresa" /></SelectTrigger>
                <SelectContent>{clientsList.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.company || c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Nombre del Negocio *</Label>
              <Input placeholder="Ej: Proyecto de Importacion Q1" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Vertical</Label>
                <Select value={formData.vertical} onValueChange={(v) => setFormData({ ...formData, vertical: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(VERTICALS).map(([k, { label }]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Valor (RD$)</Label>
                <Input type="number" min="0" value={formData.value} onChange={(e) => setFormData({ ...formData, value: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Fecha Cierre</Label>
                <Input type="date" value={formData.expected_close_date} onChange={(e) => setFormData({ ...formData, expected_close_date: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Descripcion</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button><Button type="submit">Crear Negocio</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen} itemType={deleteItem.type} itemId={deleteItem.id} itemName={deleteItem.name} isAdmin={isAdmin} onDeleted={loadData} directDelete={handleDelete} />
    </div>
  );
}
